import React, { useCallback, useMemo, memo, useEffect, useState} from 'react';
import { ColorResult, RGBColor } from 'react-color';

import { FighterOutcome, IIndexable } from '../../models/fight';

import { IField, fields, fieldsWrestling, fieldsGrappling } from './fields';
import labels from '../../utils/labels';

import { useFight } from '../../hooks/Fight';
import {
  blueCornerDomainKeys,
  domainKeys,
  getDomainStanceKeys,
  redCornerDomainKeys,
} from '../../utils/fightKeys';

import { Container, FieldContainer } from './styles';

import Chart from '../Chart';
import { keys, wrestlingKeys, grapplingKeys } from '../../utils/actionKeys';
import { toLegacyFightId } from '../../utils/legacyId';
import { sendAction } from '../../services/actions/action-sender';
import ColorPicker from '../ColorPicker';
import io from 'socket.io-client';

interface StatsProps {
  outcome: FighterOutcome;
  isRedCorner: boolean;
  color: RGBColor;
  handleChange(color: ColorResult): void;
}

interface GraphicData {
  [key: string]: number;
}

const significantStrikekRed = ['e', 'w', 'q'];
const significantStrikesBlue = ['i', 'o', 'p'];

function domainForAction(
  referenceDomain: string | null,
  isRedCornerAction: boolean,
): boolean | null {
  if (!referenceDomain) return null;
  if (redCornerDomainKeys.includes(referenceDomain)) return isRedCornerAction;
  if (blueCornerDomainKeys.includes(referenceDomain)) return !isRedCornerAction;
  return null;
}

const Stats: React.FC<StatsProps> = ({
  outcome,
  isRedCorner,
  color,
  handleChange,
}) => {
  const { fight, getFight, getRoundTimeInSeconds, updateFight } = useFight();
  // const { emit, isConnected } = useSocketV1Instance();
  // const [socket, setSocket] = useState<SocketIOClient.Socket>();
  // useEffect((): any => {
  //   const newSocket = io(process.env.REACT_APP_MIKE_API);
  //   setSocket(newSocket);

  // }, []);

  const domainsKeys = useMemo(
    () => getDomainStanceKeys(fight.fightType),
    [fight.fightType],
  );

  const actualFields = useMemo(() => {
    if (fight.fightType === 'WRESTLING') {
      return fieldsWrestling;
    } else if (fight.fightType === 'SUBMISSION_GRAPPLING') {
      return fieldsGrappling;
    }
    return fields;
  }, [fight.fightType]);

  // Condições para wrestling
  const ActionKeys = useMemo(() => {
    if (fight.fightType === 'WRESTLING') {
      return wrestlingKeys;
    } else if (fight.fightType === 'SUBMISSION_GRAPPLING') {
      return grapplingKeys;
    }
    return keys;
  }, [fight.fightType]);

  const handleClick = useCallback((field: IField) => {
    // if (fight.fightType === 'WRESTLING') {

    let keyEvent: any = '';
    if (!field.redCornerKey || !field.blueCornerKey) {
      return;
    }
    if (fight.fightType === 'WRESTLING') {
      keyEvent = isRedCorner
        ? field.redCornerButton
        : field.blueCornerButton;
    } else {
      keyEvent = isRedCorner
        ? field.redCornerKey
        : field.blueCornerKey;
    }
    // Mesma transição do teclado (`hooks/ActionKeys.tsx`): estado lido de forma
    // síncrona e domínio + stance decididos antes de emitir qualquer action.
    const current = getFight();
    const activeDomain = current.currentDomain;
    const isDomainKey = domainKeys.includes(keyEvent);

    const nextDomain = isDomainKey
      ? keyEvent === activeDomain
        ? null
        : keyEvent
      : activeDomain;
    const nextStance = isDomainKey
      ? nextDomain
        ? domainsKeys[nextDomain]
        : 'standing'
      : current.currentStance;

    let keysToEmit = [];

    keysToEmit.push(keyEvent);
    if (isDomainKey) {
      // change domain
      if (activeDomain) {
        keysToEmit.unshift(`shift+${activeDomain}`);
      }

      if (keyEvent === activeDomain) {
        keysToEmit = [];
        keysToEmit.push(`shift+${keyEvent}`);
      }
    }

    if (current.fightType !== 'WRESTLING') {
      if (significantStrikekRed.includes(keyEvent)) {
        // compute significant strikes red corner
        keysToEmit.push('shift+e');
        keysToEmit.push('r');
      }

      if (significantStrikesBlue.includes(keyEvent)) {
        // compute significant strikes blue corner
        keysToEmit.push('shift+i');
        keysToEmit.push('u');
      }
    }
    keysToEmit.forEach((key) => {

      if (!ActionKeys[key]) return;

      const isRedCornerAction = ActionKeys[key].isRedCorner;
      const isClosingKey = !!activeDomain && key === `shift+${activeDomain}`;
      const isOpeningKey = isDomainKey && !!nextDomain && key === keyEvent;

      let referenceDomain = activeDomain;
      let stance = current.currentStance;

      if (isClosingKey) {
        stance = domainsKeys[activeDomain as string];
      } else if (isOpeningKey) {
        referenceDomain = nextDomain;
        stance = nextStance;
      }

      const domain = domainForAction(referenceDomain, isRedCornerAction);

      const eventType = key === '6' ? 'reset' : 'record';
      const recordData = {
        fightId: toLegacyFightId(fight.legacyId),
        fightNewId: fight.id,
        round: current.currentRound,
        stance,
        domain,
        roundTime: getRoundTimeInSeconds(),
        name: ActionKeys[key].actionName,
        corner: ActionKeys[key].isRedCorner ? 'red' : 'blue',
        boutId: fight.oneFightId,
        eventId: fight.oneEventId || fight.eventId,
        authorId: ActionKeys[key].isRedCorner
          ? fight.legacyRedCornerId
          : fight.legacyBlueCornerId,
        targetId: ActionKeys[key].isRedCorner
          ? fight.legacyBlueCornerId
          : fight.legacyRedCornerId,
        authorNewId: ActionKeys[key].isRedCorner
          ? fight.redCornerId
          : fight.blueCornerId,
        targetNewId: ActionKeys[key].isRedCorner
          ? fight.blueCornerId
          : fight.redCornerId,
      };


      if (eventType === 'record') {
        void sendAction('record', {
          promotionId: fight.promotionId,
          data: recordData,
        });
      }
    });

    if (isDomainKey) {
      updateFight({
        currentDomain: nextDomain,
        currentStance: nextStance,
      });
    }
    // }
    }, [fight, getFight, isRedCorner, updateFight, ActionKeys, domainsKeys, getRoundTimeInSeconds]);
  return (
    <Container r={color.r} g={color.g} b={color.b} isRedCorner={isRedCorner}>
      <div className="header">
        <div className="fighter-name">
          {isRedCorner ? fight.redCornerName : fight.blueCornerName}
        </div>
        <ColorPicker
          color={color}
          isFightLoading={fight.loading || false}
          handleChange={handleChange}
        />
      </div>
      <div className="fields">
        {actualFields.standing.map((field: IField) => {
          const chartData: GraphicData = {};
          let total = 0;
          if ((outcome.standing as IIndexable)[field.fieldName] >= 0) {
            chartData.standing = (outcome.standing as IIndexable)[
              field.fieldName
            ];
            total += (outcome.standing as IIndexable)[field.fieldName];
            // console.log(field.fieldName);
          }

          if (
            fight.fightType !== 'WRESTLING' &&
            (outcome.fence as IIndexable)[field.fieldName] >= 0
          ) {
            chartData.fence = (outcome.fence as IIndexable)[field.fieldName];
            total += (outcome.fence as IIndexable)[field.fieldName];
          } else if ((outcome.riding as IIndexable)[field.fieldName] >= 0) {
            chartData.riding = (outcome.riding as IIndexable)[field.fieldName];
            total += (outcome.riding as IIndexable)[field.fieldName];
          }

          if ((outcome.ground as IIndexable)[field.fieldName] >= 0) {
            chartData.ground = (outcome.ground as IIndexable)[field.fieldName];
            total += (outcome.ground as IIndexable)[field.fieldName];
          }

          return (
            <FieldContainer
              isRedCorner={isRedCorner}
                onClick={() => handleClick(field)}
              key={`${field.fieldName}-${outcome.fighterId}`}
            >
              {fight.controlMode && fight.fightType !== 'WRESTLING' ? (
                <>
                  {field.redCornerButton && field.blueCornerButton ? (
                    <div className="stat">
                      <img
                        src={
                          isRedCorner
                            ? field.redCornerButton
                            : field.blueCornerButton
                        }
                        alt="button"
                      />
                    </div>
                  ) : (
                    <div className="stat">
                      <div className="chart">
                        <Chart
                          data={chartData}
                          title={labels[field.fieldName]}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="stat">
                  <div className="chart">
                    <Chart data={chartData} title={labels[field.fieldName]} />
                  </div>
                </div>
              )}

              <div className="stat-name">
                <span>{labels[field.fieldName]}</span>
              </div>
              <div className="chart-line">{total}</div>
            </FieldContainer>
          );
        })}
      </div>
    </Container>
  );
};

export default memo(Stats, (prevProps, nextProps) => {
  if (
    prevProps.outcome?.fighterId !== nextProps.outcome?.fighterId ||
    prevProps.isRedCorner !== nextProps.isRedCorner ||
    prevProps.color.r !== nextProps.color.r ||
    prevProps.color.g !== nextProps.color.g ||
    prevProps.color.b !== nextProps.color.b
  ) {
    return false;
  }

  if (!prevProps.outcome || !nextProps.outcome) {
    return prevProps.outcome === nextProps.outcome;
  }

  const prevOutcomeStr = JSON.stringify(prevProps.outcome);
  const nextOutcomeStr = JSON.stringify(nextProps.outcome);

  return prevOutcomeStr === nextOutcomeStr;
});
